import {Component, ElementRef, OnDestroy, OnInit, ViewChild, ViewEncapsulation} from '@angular/core';
import {AuthService} from '../../../services/auth.service';
import {
    fadeInLeftOnEnterAnimation,
    fadeInOnEnterAnimation,
    fadeInUpBigOnEnterAnimation,
    fadeOutOnLeaveAnimation,
    fadeOutRightOnLeaveAnimation,
    headShakeAnimation,
} from 'angular-animations';
import {Subscription} from 'rxjs';
import {EventsService} from '../../../services/events.service';
import {environment} from '../../../../environments/environment';
import {AnalyticsService} from '../../../services/analytics.service';
import {CoachService} from '../service/coach.service';
import {
    FormGroup,
    NgForm,
} from '@angular/forms';
import {CoachingJourneyDelegateFormModel, CoachingJourneyGoalFormModel} from '../models/journey.model';
import {RequesterResponseModel} from '../../../services/models/requester.model';
import {UserService} from '../../../services/user.service';
import {NavService} from '../../../services/nav.service';
import {AlertController, IonAccordionGroup, IonContent, LoadingController, Platform} from '@ionic/angular';
import {TranslateService} from '@ngx-translate/core';
import {ActivatedRoute} from '@angular/router';

@Component({
    selector: 'app-coach-journey-page',
    templateUrl: 'coach-journey.page.html',
    styleUrls: ['coach-journey.page.scss'],
    animations: [
        fadeInOnEnterAnimation({anchor: 'enter', duration: 500}),
        fadeOutOnLeaveAnimation({anchor: 'leave', duration: 500}),
        fadeOutRightOnLeaveAnimation({anchor: 'leaveList', duration: 300}),
        fadeInLeftOnEnterAnimation({anchor: 'enterList', duration: 300}),
        fadeInUpBigOnEnterAnimation({anchor: 'enterUp', duration: 300}),
        headShakeAnimation(),
    ]
})
export class CoachJourneyPage implements OnInit, OnDestroy {
    subscriptions: Subscription[] = [];

    @ViewChild('form', {static: false}) form: NgForm;
    @ViewChild(IonContent, { static: false }) ionContent: IonContent;
    @ViewChild('firstBar', { static: false }) firstBarElement: ElementRef;
    @ViewChild('secondBar', { static: false }) secondBarElement: ElementRef;
    @ViewChild('thirdBar', { static: false }) thirdBarElement: ElementRef;

    environment = null;
    modalIsVisible = false;

    public animationState = false;
    platform = 'website';

    isShowCompletedGoals: boolean = false;
    isShowCompletedAppointments = false;

    userType = null;
    level = 'learner';
    delegateId: number = null;

    report: CoachingJourneyDelegateFormModel = null;
    reportPromise: Promise<CoachingJourneyDelegateFormModel> = null;
    reportValid = false;

    goalsNumber = 0;

    isSaved = true;
    isSaving = false;

    secondAccordionType = 'list';
    templateCoachId = null;
    secondAccordionBack = 'list';

    isFirst = false;
    isSecond = false;
    isThird = false;

    constructor(
        private event: EventsService,
        private activatedRoute: ActivatedRoute,
        public authService: AuthService,
        public userService: UserService,
        private coachService: CoachService,
        private analytics: AnalyticsService,
        private nav: NavService,
        private alertController: AlertController,
        private platformService: Platform,
        private translate: TranslateService,
        private loadingCtrl: LoadingController
    ) {
        this.environment = environment;
        this.platform = this.authService.appMobile ? 'mobile' : 'website';

        this.subscriptions.push(
            this.event.subscribe('login', () => {
                this.reportValid = false;
            })
        );
        this.subscriptions.push(
            this.event.subscribe('logout', () => {
                this.reportValid = false;
            })
        );
        this.subscriptions.push(
            this.event.subscribe('coaching-journey.updateMyCoachingJourneyGoalStatus', () => {
                this.reportValid = false
            })
        );

        this.subscriptions.push(
            this.event.subscribe('coach:addCoachAppointment', () => {
                this.reportValid = false
                this.coachService.getUserCoachingJourneyForm(this.delegateId).then(data => {
                    this.getUserCoachingJourneyFormHandler(data);
                });
            })
        );
        this.subscriptions.push(
            this.event.subscribe('coach:updateSessionStatus', () => {
                this.reportValid = false
                this.coachService.getUserCoachingJourneyForm(this.delegateId).then(data => {
                    this.getUserCoachingJourneyFormHandler(data);
                });
            })
        );
        this.subscriptions.push(
            this.event.subscribe('form:changed', (formName: string) => {
                if (formName === 'coaching-journey') {
                    this.isSaved = false;
                }
            })
        );
    }

    ngOnInit(): void {
        this.delegateId = Number(this.activatedRoute.snapshot.paramMap.get('delegateId'));
        this.level = this.activatedRoute.snapshot.data.level;
    }

    ionViewWillEnter() {
        this.isSaved = true;
        // this.menuVisible = false;
        this.userType = this.authService.getUserType();

        if (this.report === null || !this.reportValid) {
            this.reportPromise = this.coachService.getUserCoachingJourneyForm(this.delegateId);
        }
    }

    ionViewDidEnter() {
        this.analytics.trackView('Your Coaching Journey');

        if (this.report === null || !this.reportValid) {
            this.reportPromise.then(data => {
                this.getUserCoachingJourneyFormHandler(data);
            });
        }
    }

    ngAfterViewInit() {
        setTimeout(() => {
            const shadowRoot = this.ionContent['el'].shadowRoot;

            if (shadowRoot) {
                const innerScroll = shadowRoot.querySelector('.inner-scroll');

                if (innerScroll) {
                    (innerScroll as HTMLElement).style.zIndex = '0';
                }
            }
        }, 100);
    }

    getUserCoachingJourneyFormHandler(data) {
        if (data === null) {
            this.alertController.create({
                header: this.translate.instant('__ALERT_ERROR_TITLE__'),
                message: this.translate.instant('__ALERT_ERROR_EMPTY_DATA_MESSAGE__'),
                buttons: [
                    {
                        text: this.translate.instant('__ALERT_ERROR_BUTTON__'),
                        handler: () => {
                            this.goBack();
                        }
                    }]
            }).then(a => a.present());
        } else {
            this.report = data;
            this.reportValid = true;
            this.goalsNumber = this.report.goals.value.length;
            if (!!this.report.coach) {
                this.templateCoachId = this.report.coach.id;
                this.secondAccordionBack = 'profile';
                this.secondAccordionType = 'calendar'
            }
            if (this.goalsNumber === 0 && this.level === 'learner') {
                this.toggleAccordion();
            } else {
                this.toggleAccordionByFirst();
            }
        }
    }

    addGoal() {
        if (this.level === 'manager') {
            return false;
        }

        this.report.goals.value = [
            ...this.report.goals.value,
            JSON.parse(JSON.stringify(this.report.goals.empty))
        ];
    }

    onProgressChange(event, goal: CoachingJourneyGoalFormModel) {
        goal.status = goal.progress.value === 100;
        this.onFormChange(event);
    }

    onGoalStatusChange(goal: CoachingJourneyGoalFormModel, status: boolean) {
        if (this.level !== 'learner') {
            return false;
        }

        if (!this.isSaved) {
            this.alertController.create({
                header: this.translate.instant('__ALERT_UNSAVED_TITLE__'),
                message: this.translate.instant('__REVIEW_ALERT_UNSAVED_MESSAGE__'),
                buttons: [this.translate.instant('__ALERT_UNSAVED_BUTTON_STAY__')]
            }).then(a => a.present());
        } else {
            this.alertController.create({
                header: this.translate.instant('__REVIEW_GOAL_STATUS_CHANGE_TITLE__'),
                message: this.translate.instant(status ?
                    '__REVIEW_GOAL_STATUS_CHANGE_INCOMPLETE_MESSAGE__' :
                    '__REVIEW_GOAL_STATUS_CHANGE_COMPLETE_MESSAGE__'),
                buttons: [{
                    text: this.translate.instant('__REVIEW_GOAL_STATUS_CHANGE_BUTTON__'),
                    cssClass: 'alert-controller-warning',
                    handler: () => {
                        this.changeGoalStatus(goal.id, !status);
                    }
                }, this.translate.instant('__ALERT_CANCEL_BUTTON__')]
            }).then(a => a.present());
        }
    }

    changeGoalStatus(goal_id: number, status: boolean) {
        if (this.level === 'manager') {
            return false;
        }

        this.showLoading();
        this.coachService.updateUserCoachingJourneyGoalStatus(goal_id, status).then(data => {
            if (!!data.status && data.status === 'ok') {
                this.reportValid = false;
                this.coachService.getUserCoachingJourneyForm(this.delegateId).then(data => {
                    this.getUserCoachingJourneyFormHandler(data);
                });
            }
        });
    }

    onRemoveGoal(index: number, item: CoachingJourneyGoalFormModel) {
        if (this.level === 'manager') {
            return false;
        }

        if (item.id && !this.isSaved) {
            this.alertController.create({
                header: this.translate.instant('__ALERT_UNSAVED_TITLE__'),
                message: this.translate.instant('__REVIEW_ALERT_UNSAVED_MESSAGE__'),
                buttons: [this.translate.instant('__ALERT_UNSAVED_BUTTON_STAY__')]
            }).then(a => a.present());
            return false;
        }

        this.alertController.create({
            header: this.translate.instant('__REVIEW_FORM_GOAL_DELETE_TITLE__'),
            message: this.translate.instant('__REVIEW_FORM_GOAL_DELETE_MESSAGE__'),
            buttons: [{
                text: this.translate.instant('__REVIEW_FORM_GOAL_DELETE_BUTTON__'),
                cssClass: 'alert-controller-warning',
                handler: () => {
                    this.showLoading();
                    if (!item.id) {
                        this.report.goals.value = [...this.report.goals.value.filter((item, i) => i !== index)];
                    } else {
                        this.removeGoal(item.id, item.status);
                    }
                }
            }, this.translate.instant('__ALERT_CANCEL_BUTTON__')]
        }).then(a => a.present());
    }

    removeGoal(goal_id: number, status: boolean) {
        this.toggleAccordionByFirst();

        if (this.level === 'manager') {
            return false;
        }

        this.coachService.deleteUserCoachingJourneyGoal(goal_id).then(data => {
            if (!!data.status && data.status === 'ok') {
                this.reportValid = false;
                this.coachService.getUserCoachingJourneyForm(this.delegateId).then(data => {
                    this.getUserCoachingJourneyFormHandler(data);
                });
            }
        });
    }

    toggleAccordion() {
        this.isFirst = false;
        this.isSecond = false;
        this.isThird = false;
    }

    toggleAccordionByFirst = () => {
        this.isFirst = true;
        this.isSecond = false;
        this.isThird = false;
    };

    onSubmit() {
        if (this.report.goals.value.length > 0) {
            this.toggleAccordionByFirst();
        } else {
            this.toggleAccordion();
        }

        Object.keys(this.form.controls).forEach(key => {
            if (this.form.controls[key] instanceof FormGroup) {
                Object.keys(this.form.controls[key]['controls']).forEach(key2 => {
                    this.form.controls[key]['controls'][key2].markAsTouched();
                })
            } else {
                this.form.controls[key].markAsTouched();
            }
        });

        if (this.form.invalid) {
            this.displayErrorAlert('__ALERT_ERROR_MESSAGE_FILL_REQUIRED_FIELDS__');
            return;
        }

        this.showLoading();
        this.isSaving = true;
        this.coachService.saveUserCoachingJourneyForm(this.report).then(data => {
            this.isSaving = false;
            if (data.hasOwnProperty('status') && data['status'] === 'ok') {
                this.isSaved = true;
                this.event.publish('form:saved', {name: 'coaching-journey'});
                this.displaySuccessAlert((data as RequesterResponseModel).message);

                this.reportValid = false;
                this.coachService.getUserCoachingJourneyForm(this.delegateId).then(data => {
                    this.getUserCoachingJourneyFormHandler(data);
                });
            } else {
                this.report = data as CoachingJourneyDelegateFormModel;
                this.displayErrorAlert('__ALERT_ERROR_MESSAGE_FILL_REQUIRED_FIELDS__');
            }
        }).catch(() => {
            this.isSaving = false;
        });
    }

    chooseNewCoach() {
        if (this.report.coaching_info.remaining === 0) {
            this.displayErrorAlert('Please cancel any booked or pending requests before changing coach', '');
            return;
        }

        this.secondAccordionType = 'list';
        this.closeAccordion('third');
        this.openAccordion('second');
    }

    onAccordionClick(status, name) {
        this.isAccordionClickEnabled(name);

        if (name === 'first') {
            this.isFirst = status;
            if (!this.report.goals.value.length && this.level === 'learner') {
                this.addGoal();
            }
        } else if (name === 'second') {
            this.isSecond = status;
            if (status) {
                this.isThird = false;
                if (!!this.report.coach) {
                    this.templateCoachId = this.report.coach.id;
                    this.secondAccordionBack = 'profile';
                    this.secondAccordionType = 'calendar'
                } else {
                    this.secondAccordionType = 'list';
                }
            }
        } else if (name === 'third') {
            this.isThird = status;
            if (status) {
                this.isSecond = false;
            }
        }
        this.scrollToBar(name);
    }

    isAccordionClickEnabled(name) {
        if (name === 'second') {
            if (this.goalsNumber === 0) {
                this.displayErrorAlert('Please add and save your first goal to access this section', '');
                return false;
            } else if (!this.report || this.report.coaching_info.remaining === 0) {
                return false;
            }
        }
        if (name === 'third') {
            if (this.report.coaching_info.total === 0) {
                this.displayErrorAlert('This section cannot be accessed until you have booked a coaching call', '');
                return false;
            }
        }
        return true;
    }

    openAccordion(name) {
        if (!this.isAccordionActive(name) && this.isAccordionClickEnabled(name)) {
            if (name === 'first') {
                this.isFirst = true;
            } else if (name === 'second') {
                this.isSecond = true;
            } else if (name === 'third') {
                this.isThird = true;
            }
        }
        this.scrollToBar(name);
    }

    scrollToBar(name): void {
        setTimeout(() => {
            if (name === 'first') {
                this.firstBarElement.nativeElement.scrollIntoView({behavior: 'smooth', block: 'start'});
            } else if (name === 'second') {
                this.secondBarElement.nativeElement.scrollIntoView({behavior: 'smooth', block: 'start'});
            } else if (name === 'third') {
                this.thirdBarElement.nativeElement.scrollIntoView({behavior: 'smooth', block: 'start'});
            }
        }, 100);
    }

    closeAccordion(name) {
        if (this.isAccordionActive(name)) {
            if (name === 'first') {
                this.isFirst = false;
            } else if (name === 'second') {
                this.isSecond = false;
            } else if (name === 'third') {
                this.isThird = false;
            }
        }
    }

    isAccordionActive(name) {
        if (name === 'first') {
            return this.isFirst;
        } else if (name === 'second') {
            return this.isSecond;
        } else if (name === 'third') {
            return this.isThird;
        }
        return false;
    }

    async showLoading() {
        const loading = await this.loadingCtrl.create({
            message: 'Loading...',
            duration: 1000,
            spinner: 'circles',
        });

        loading.present();
    }

    displayErrorAlert(message: string, header: string = 'default') {
        this.alertController.create({
            header: header === 'default' ? this.translate.instant('__ALERT_ERROR_TITLE__') : header,
            message: this.translate.instant(message),
            buttons: [this.translate.instant('__ALERT_ERROR_BUTTON__')]
        }).then(a => a.present());
    }

    displaySuccessAlert(message: string) {
        this.alertController.create({
            header: this.translate.instant('__ALERT_SUCCESS_TITLE__'),
            message: this.translate.instant(message),
            buttons: [this.translate.instant('__ALERT_SUCCESS_BUTTON__')]
        }).then(a => a.present());
    }

    getSecondTitle() {
        if (this.goalsNumber === 0) {
            return this.translate.instant('__COACHING_JOURNEY_BOX2_TITLE1__');
        } else if (this.goalsNumber > 0 && this.report.coach === null) {
            return this.translate.instant('__COACHING_JOURNEY_BOX2_TITLE2__');
        } else if (this.goalsNumber > 0 && this.report.coach !== null) {
            return this.translate.instant('__COACHING_JOURNEY_BOX2_TITLE3__', {
                'full_name': this.report.coach.full_name
            }) + (this.report.coaching_info.remaining !== null ? " " + this.translate.instant('__COACHING_JOURNEY_BOX2_TITLE3_REMAINING__', {
                'remaining': this.report.coaching_info.remaining
            }) : '');
        }
    }

    getBackRoute() {
        if (this.level === 'manager') {
            return ['/team/'];
        } else if (this.level === 'coach') {
            return ['/coach/requests-incoming/'];
        } else {
            return this.nav.getHomeCommand()
        }
    }


    goBack() {
        this.nav.navigate(this.getBackRoute());
    }

    onFormChange(event) {
        this.event.publish('form:changed', 'coaching-journey');
    }

    isLearner(){
        return (this.level === 'learner');
    }
    isManager(){
        return (this.level === 'manager');
    }
    isCoach(){
        return (this.level === 'coach');
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach((i) => i.unsubscribe());
    }
}
